import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Users2, Plus, Edit, Trash2, UserPlus, Search, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

interface UserGroup {
  id: string;
  name: string;
  description: string | null;
  district_id: string;
  group_type: 'plc' | 'district' | 'school';
  school_name: string | null;
  is_active: boolean;
  created_by: string;
  created_at: string;
  member_count?: number;
}

interface GroupMember {
  id: string;
  user_id: string;
  role: string;
  joined_at: string;
  profiles?: {
    first_name: string;
    last_name: string;
    email: string;
    school_name: string;
  };
}

interface UserGroupManagerProps {
  districtId?: string;
  onSave: (groupData: any) => void;
}

export default function UserGroupManager({ districtId, onSave }: UserGroupManagerProps) {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<UserGroup | null>(null);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [searchFilter, setSearchFilter] = useState<'district' | 'school'>('district');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    group_type: 'plc' as 'plc' | 'district' | 'school',
    school_name: '',
  });

  const { user } = useAuth();

  useEffect(() => {
    loadCurrentUserProfile();
  }, [user]);

  useEffect(() => {
    if (currentUserProfile) {
      loadGroups();
    }
  }, [districtId, currentUserProfile]);

  const loadCurrentUserProfile = async () => {
    if (!user?.id) return;

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      // Check if user is admin
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('is_active', true);

      setCurrentUserProfile(profile);
      setIsAdmin(roles?.some((r: any) => r.role === 'admin') || false);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const loadGroups = async () => {
    try {
      setLoading(true);
      const userDistrictId = districtId || currentUserProfile?.district_id;
      
      if (!userDistrictId) {
        setGroups([]);
        return;
      }

      const { data, error } = await supabase
        .from('user_groups')
        .select('*')
        .eq('district_id', userDistrictId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get member counts
      const groupsWithCounts = await Promise.all(
        (data || []).map(async (group) => {
          const { count } = await supabase
            .from('user_group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id);
          
          return {
            ...group,
            group_type: group.group_type as 'plc' | 'district' | 'school',
            member_count: count || 0,
          };
        })
      );

      setGroups(groupsWithCounts as UserGroup[]);
    } catch (error: any) {
      console.error('Error loading groups:', error);
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    try {
      const userDistrictId = districtId || currentUserProfile?.district_id;
      
      if (!userDistrictId) {
        toast.error('District ID is required');
        return;
      }

      const { data, error } = await supabase
        .from('user_groups')
        .insert([
          {
            name: formData.name,
            description: formData.description,
            district_id: userDistrictId,
            group_type: formData.group_type,
            school_name: formData.group_type === 'school' ? formData.school_name : null,
            created_by: user?.id,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      toast.success('Professional Learning Community created successfully');
      setShowCreateDialog(false);
      resetForm();
      loadGroups();
      onSave(data);
    } catch (error: any) {
      console.error('Error creating group:', error);
      toast.error(error.message || 'Failed to create group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !formData.name.trim()) return;

    try {
      const { data, error } = await supabase
        .from('user_groups')
        .update({
          name: formData.name,
          description: formData.description,
          group_type: formData.group_type,
          school_name: formData.group_type === 'school' ? formData.school_name : null,
        })
        .eq('id', editingGroup.id)
        .select()
        .single();

      if (error) throw error;

      toast.success('Group updated successfully');
      setEditingGroup(null);
      resetForm();
      loadGroups();
    } catch (error: any) {
      console.error('Error updating group:', error);
      toast.error('Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    try {
      const { error } = await supabase
        .from('user_groups')
        .delete()
        .eq('id', groupId);

      if (error) throw error;

      toast.success('Group deleted successfully');
      loadGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    }
  };

  const openEditDialog = (group: UserGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      group_type: group.group_type,
      school_name: group.school_name || '',
    });
  };

  const openMembersDialog = async (group: UserGroup) => {
    setSelectedGroup(group);
    setShowMembersDialog(true);
    await loadGroupMembers(group.id);
    await loadAvailableUsers();
  };

  const loadGroupMembers = async (groupId: string) => {
    try {
      const { data: members, error } = await supabase
        .from('user_group_members')
        .select('*')
        .eq('group_id', groupId);

      if (error) throw error;

      // Fetch profile data separately
      const membersWithProfiles = await Promise.all(
        (members || []).map(async (member) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, email, school_name')
            .eq('id', member.user_id)
            .single();

          return {
            ...member,
            profiles: profile,
          };
        })
      );

      setGroupMembers(membersWithProfiles as GroupMember[]);
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Failed to load group members');
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const userDistrictId = districtId || currentUserProfile?.district_id;
      let query = supabase
        .from('profiles')
        .select('id, first_name, last_name, email, school_name')
        .eq('district_id', userDistrictId);

      if (searchFilter === 'school' && currentUserProfile?.school_name) {
        query = query.eq('school_name', currentUserProfile.school_name);
      }

      if (searchQuery) {
        query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`);
      }

      const { data, error } = await query
        .order('last_name')
        .limit(50);

      if (error) throw error;
      
      // Filter out users already in the group
      const existingMemberIds = new Set(groupMembers.map(m => m.user_id));
      const filtered = (data || []).filter(u => !existingMemberIds.has(u.id));
      
      setAvailableUsers(filtered);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleAddMembers = async () => {
    if (!selectedGroup || selectedUsers.size === 0) return;

    try {
      const members = Array.from(selectedUsers).map(userId => ({
        group_id: selectedGroup.id,
        user_id: userId,
        role: 'member',
      }));

      const { error } = await supabase
        .from('user_group_members')
        .insert(members);

      if (error) throw error;

      toast.success(`Added ${selectedUsers.size} member(s) to group`);
      setSelectedUsers(new Set());
      await loadGroupMembers(selectedGroup.id);
      await loadAvailableUsers();
      await loadGroups();
    } catch (error: any) {
      console.error('Error adding members:', error);
      toast.error(error.message || 'Failed to add members');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('user_group_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast.success('Member removed from group');
      if (selectedGroup) {
        await loadGroupMembers(selectedGroup.id);
        await loadGroups();
      }
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      group_type: 'plc',
      school_name: '',
    });
    setEditingGroup(null);
  };

  const getGroupTypeLabel = (type: string) => {
    switch (type) {
      case 'plc': return 'Professional Learning Community';
      case 'school': return 'School Group';
      case 'district': return 'District Group';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users2 className="h-5 w-5" />
              Professional Learning Communities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users2 className="h-5 w-5" />
              Professional Learning Communities
            </div>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Group
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Group</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Group Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Math PLC, Science Dept"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe the purpose of this group"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">Group Type *</Label>
                    <Select 
                      value={formData.group_type} 
                      onValueChange={(value: any) => setFormData({ ...formData, group_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="plc">Professional Learning Community</SelectItem>
                        <SelectItem value="school">School Group</SelectItem>
                        {isAdmin && <SelectItem value="district">District Group</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.group_type === 'school' && (
                    <div>
                      <Label htmlFor="school">School Name</Label>
                      <Input
                        id="school"
                        value={formData.school_name}
                        onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                        placeholder="Enter school name"
                      />
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateGroup}>
                      Create Group
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users2 className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg font-medium mb-2">No groups created yet</p>
              <p className="text-sm mb-4">Create your first Professional Learning Community to collaborate with colleagues</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Group
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">Members</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell className="font-medium">{group.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-xs truncate">
                      {group.description || 'No description'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {getGroupTypeLabel(group.group_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">{group.member_count || 0}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(group.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openMembersDialog(group)}
                          title="Manage members"
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(group)}
                          title="Edit group"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" title="Delete group">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Group</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{group.name}"? This will remove all members and cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteGroup(group.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingGroup} onOpenChange={() => setEditingGroup(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Group Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="edit-type">Group Type</Label>
              <Select 
                value={formData.group_type} 
                onValueChange={(value: any) => setFormData({ ...formData, group_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plc">Professional Learning Community</SelectItem>
                  <SelectItem value="school">School Group</SelectItem>
                  {isAdmin && <SelectItem value="district">District Group</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            {formData.group_type === 'school' && (
              <div>
                <Label htmlFor="edit-school">School Name</Label>
                <Input
                  id="edit-school"
                  value={formData.school_name}
                  onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
                />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingGroup(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateGroup}>
                Update Group
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={showMembersDialog} onOpenChange={setShowMembersDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Members - {selectedGroup?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Current Members */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Current Members ({groupMembers.length})</h3>
              {groupMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No members yet</p>
              ) : (
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {groupMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3">
                      <div>
                        <p className="font-medium text-sm">
                          {member.profiles?.first_name} {member.profiles?.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">{member.profiles?.email}</p>
                        {member.profiles?.school_name && (
                          <p className="text-xs text-muted-foreground">{member.profiles.school_name}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Members */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Add Members</h3>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Select value={searchFilter} onValueChange={(v: any) => setSearchFilter(v)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="district">All District</SelectItem>
                      <SelectItem value="school">My School</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button onClick={loadAvailableUsers} variant="secondary">
                    <Search className="h-4 w-4" />
                  </Button>
                </div>

                {availableUsers.length > 0 && (
                  <>
                    <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                      {availableUsers.map((user) => (
                        <div key={user.id} className="flex items-center gap-3 p-3">
                          <Checkbox
                            checked={selectedUsers.has(user.id)}
                            onCheckedChange={() => toggleUserSelection(user.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-sm">
                              {user.first_name} {user.last_name}
                            </p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                            {user.school_name && (
                              <p className="text-xs text-muted-foreground">{user.school_name}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="text-sm text-muted-foreground">
                        {selectedUsers.size} user(s) selected
                      </p>
                      <Button 
                        onClick={handleAddMembers}
                        disabled={selectedUsers.size === 0}
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Selected
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}